import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Pause, Play, RotateCcw, MonitorPlay } from 'lucide-react';
import { SCENES, PRESENTATION, TOTAL_SECONDS } from '../presentationConfig';
import { openChannel } from '../engine/presenterChannel';
import { fmtClock } from '../engine/Stage';

// Second-window presenter view: mirrors the stage state via BroadcastChannel.
export default function PresenterRemote() {
  const [state, setState] = useState(null);
  const ch = useRef(null);
  useEffect(() => {
    ch.current = openChannel();
    if (!ch.current) return undefined;
    ch.current.onmessage = (e) => { if (e.data?.type === 'state') setState(e.data.state); };
    return () => ch.current?.close();
  }, []);
  const send = (cmd, arg) => ch.current?.postMessage({ cmd, arg });
  const scene = state ? SCENES[state.index] : null;
  const remaining = state ? Math.max(0, PRESENTATION.sceneDuration - state.t) : PRESENTATION.sceneDuration;

  return (
    <div className="pk-remote" data-testid="pk-presenter-remote">
      <header>
        <img src="/nichecom-logo-nav.png" alt="NicheCom" style={{ height: 30 }} />
        <span>Presenter view</span>
        <button className="pk-btn pk-btn-lime" onClick={() => window.open('/jackson-stops-presentation/present', 'pk-audience')}><MonitorPlay size={16} /> Open audience window</button>
      </header>
      {!state ? (
        <div className="pk-remote-wait">Waiting for the audience window… open it from the button above, then press Begin there (or here once connected).</div>
      ) : (
        <>
          <div className="pk-remote-grid">
            <div className="pk-glass pk-remote-card">
              <div className="pk-remote-k">Now · {String(state.index + 1).padStart(2, '0')} / {SCENES.length}</div>
              <div className="pk-remote-title">{scene?.title}</div>
              <div className="pk-remote-note">{scene?.note}</div>
            </div>
            <div className="pk-glass pk-remote-card pk-remote-timer">
              <div className="pk-remote-k">Scene remaining</div>
              <div className={`pk-remote-big ${remaining <= 3 ? 'warn' : ''}`}>{fmtClock(remaining)}</div>
              <div className="pk-remote-k">Elapsed {fmtClock(state.overall)} / {fmtClock(TOTAL_SECONDS)}</div>
            </div>
            <div className="pk-glass pk-remote-card">
              <div className="pk-remote-k">Next</div>
              <div className="pk-remote-title">{state.next}</div>
              <div className="pk-remote-note">{SCENES[state.index + 1]?.note || 'Hold the final frame.'}</div>
            </div>
            {scene?.script && (
              <div className="pk-glass pk-remote-card pk-remote-script" data-testid="pk-remote-script">
                <div className="pk-remote-k">Script · {String(state.index + 1).padStart(2, '0')}</div>
                <p>{scene.script}</p>
              </div>
            )}
          </div>
          <div className="pk-remote-buttons">
            {!state.began && <button className="pk-btn pk-btn-lime" onClick={() => send('begin')}>Begin</button>}
            <button onClick={() => send('prev')}><ChevronLeft size={22} /></button>
            <button className="pk-ctrl-main" onClick={() => send('toggle')}>{state.playing ? <Pause size={22} /> : <Play size={22} />}</button>
            <button onClick={() => send('next')}><ChevronRight size={22} /></button>
            <button onClick={() => send('restart')}><RotateCcw size={20} /></button>
          </div>
          <div className="pk-remote-scenes">
            {SCENES.map((s, i) => (
              <button key={s.id} className={i === state.index ? 'active' : ''} onClick={() => send('goto', i)}>{String(s.n).padStart(2, '0')}</button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
